import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { reportAdminService } from '../../services/reportAdminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { ConfirmDialog } from '../shared/ConfirmDialog';

export function UserReportAssignments() {
  const [users, setUsers] = useState([]);
  const [systems, setSystems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  // Per-cell in-flight guard. Key: `${userId}:${systemId}`.
  const [pendingToggles, setPendingToggles] = useState(new Set());
  const [confirmReset, setConfirmReset] = useState(false);

  const loadData = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      const [usersData, systemsData, assignmentsData] = await Promise.all([
        reportAdminService.getAllUsers(),
        reportAdminService.getReportSystems(),
        reportAdminService.getUserReportAssignments(),
      ]);
      if (isCancelled()) return;
      setUsers(usersData);
      setSystems(systemsData.filter((s) => s.is_active));
      setAssignments(assignmentsData);
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(loadData, []);

  const isAssigned = (userId, systemId) => {
    return assignments.some(
      (a) => a.user_id === userId && a.report_system_id === systemId && a.is_active
    );
  };

  const handleToggle = async (userId, systemId) => {
    const key = `${userId}:${systemId}`;
    if (pendingToggles.has(key)) return; // in-flight guard

    const previousAssignments = assignments;
    const wasAssigned = isAssigned(userId, systemId);

    if (wasAssigned) {
      setAssignments((prev) =>
        prev.filter((a) => !(a.user_id === userId && a.report_system_id === systemId))
      );
    } else {
      setAssignments((prev) => [
        ...prev,
        { id: `optimistic-${key}`, user_id: userId, report_system_id: systemId, is_active: true },
      ]);
    }
    setPendingToggles((prev) => new Set(prev).add(key));

    try {
      setError(null);
      if (wasAssigned) {
        await reportAdminService.removeReportSystemFromUser(userId, systemId);
      } else {
        await reportAdminService.assignReportSystemToUser(userId, systemId);
      }
    } catch (err) {
      setAssignments(previousAssignments);
      toast.error(mapSupabaseError(err));
    } finally {
      setPendingToggles((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Bumps due_since to now() on all the selected user's report assignments
  // (their past-due stats restart from this moment).
  const handleResetPastDue = async () => {
    const user = selectedUser;
    if (!user) return;
    const key = `reset:${user.id}`;
    setConfirmReset(false);
    if (pendingToggles.has(key)) return;
    setPendingToggles((prev) => new Set(prev).add(key));
    try {
      await reportAdminService.resetReportPastDue(user.id);
      toast.success(`Past-due stats reset for ${user.name}`);
    } catch (err) {
      toast.error(mapSupabaseError(err));
    } finally {
      setPendingToggles((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="user-report-assignments">
      <h2>User Report Assignments</h2>
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="assignments-container">
        <div className="users-list">
          <h3>Users</h3>
          <div className="user-selector">
            {users.map((user) => (
              <div
                key={user.id}
                className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => setSelectedUser(user)}
              >
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
                <div className="user-role">{user.role}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="assignments-grid">
          <h3>Assignments Matrix</h3>
          {selectedUser ? (
            <div className="assignment-form">
              <div className="assignment-form-header">
                <h4>{selectedUser.name}'s Systems</h4>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmReset(true)}
                  disabled={pendingToggles.has(`reset:${selectedUser.id}`)}
                >
                  Reset Past Due
                </button>
              </div>
              <div className="system-checkboxes">
                {systems.map((system) => {
                  const key = `${selectedUser.id}:${system.id}`;
                  return (
                    <label key={system.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={isAssigned(selectedUser.id, system.id)}
                        onChange={() => handleToggle(selectedUser.id, system.id)}
                        disabled={pendingToggles.has(key)}
                      />
                      <span>{system.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <p>Select a user to manage their system assignments</p>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmReset}
        title="Reset Past Due"
        message={`Reset past-due stats for ${selectedUser?.name}? Their past-due count restarts at 0 from now; the next due report is the first full period from now.`}
        confirmText="Reset"
        onConfirm={handleResetPastDue}
        onCancel={() => setConfirmReset(false)}
      />
    </div>
  );
}
